import React, { useEffect, useState } from 'react';
import { Bot, ShieldAlert, ShieldCheck, Play, Loader2, Server } from 'lucide-react';

/**
 * ChatBot / IA Pentest — teste la robustesse d'un agent conversationnel / LLM cible
 * face à l'OWASP LLM Top 10 (prompt injection, jailbreak, fuite de system prompt, etc.).
 *
 * Scan PROGRESSIF : une requête courte par sonde (évite les timeouts nginx),
 * résultats affichés au fil de l'eau + barre de progression.
 * Backend : /api/v1/chatbot-pentest/{presets,tests,scan}
 */
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token') || ''}`,
});

const sevColor = (sev, vuln) => {
  if (!vuln) return 'text-green-400 border-green-600/40';
  if (sev === 'CRITICAL') return 'text-red-400 border-red-600/50';
  if (sev === 'HIGH') return 'text-orange-400 border-orange-600/50';
  return 'text-yellow-400 border-yellow-600/50';
};

const ChatbotPentest = () => {
  const [presets, setPresets] = useState([]);
  const [tests, setTests] = useState([]);
  const [target, setTarget] = useState({ type: 'ollama', url: 'http://ollama:11434', model: 'sylink/sylink2:27b' });
  const [manual, setManual] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [findings, setFindings] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/api/v1/chatbot-pentest/presets', { headers: authHeaders() })
      .then(r => r.json()).then(d => setPresets(d.presets || [])).catch(() => {});
    fetch('/api/v1/chatbot-pentest/tests', { headers: authHeaders() })
      .then(r => r.json()).then(d => setTests(d.tests || [])).catch(() => {});
  }, []);

  const applyPreset = (id) => {
    const p = presets.find(x => x.id === id);
    if (p) { setTarget({ type: p.type, url: p.url, model: p.model }); setManual(false); }
  };

  const runScan = async () => {
    setRunning(true); setError(null); setFindings([]);
    const probes = tests.length ? tests : [{ id: null }];
    setProgress({ done: 0, total: probes.length });
    try {
      for (let i = 0; i < probes.length; i++) {
        const res = await fetch('/api/v1/chatbot-pentest/scan', {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ target, probe_ids: probes[i].id ? [probes[i].id] : null }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setFindings(prev => [...prev, ...(data.findings || [])]);
        setProgress({ done: i + 1, total: probes.length });
      }
    } catch (e) {
      setError(e.message || 'Erreur de scan');
    } finally {
      setRunning(false);
    }
  };

  const vulnCount = findings.filter(f => f.vulnerable).length;
  const score = findings.length ? Math.max(0, 100 - vulnCount * 12 - findings.filter(f => f.vulnerable && ['CRITICAL', 'HIGH'].includes(f.severity)).length * 8) : null;

  return (
    <div className="space-y-6">
      {/* Cible */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4 text-white font-semibold">
          <Bot className="w-5 h-5 text-red-500" /> Cible ChatBot / Agent IA
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Agent interne SYLink</label>
            <select onChange={e => applyPreset(e.target.value)}
              className="bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-200">
              <option value="">— choisir un preset —</option>
              {presets.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <button onClick={() => setManual(!manual)}
            className="text-xs text-red-400 underline">{manual ? 'masquer' : 'cible manuelle'}</button>
        </div>
        {manual && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Type</label>
              <select value={target.type} onChange={e => setTarget({ ...target, type: e.target.value })}
                className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-200">
                <option value="ollama">ollama</option>
                <option value="openai">openai</option>
                <option value="http">http générique</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">URL</label>
              <input value={target.url} onChange={e => setTarget({ ...target, url: e.target.value })}
                className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-200" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Modèle</label>
              <input value={target.model || ''} onChange={e => setTarget({ ...target, model: e.target.value })}
                className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-200" />
            </div>
          </div>
        )}
        <div className="flex items-center gap-3 mt-4 text-sm text-gray-400">
          <Server className="w-4 h-4" /> {target.type} · {target.url} {target.model ? `· ${target.model}` : ''}
        </div>
        <button onClick={runScan} disabled={running}
          className="mt-4 flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-red-600 text-white hover:bg-red-500 disabled:opacity-50">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? `Scan en cours… ${progress.done}/${progress.total}` : 'Lancer le scan OWASP LLM'}
        </button>
        {error && <div className="mt-3 text-sm text-red-400">⚠️ {error}</div>}
      </div>

      {/* Score */}
      {score !== null && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 flex items-center gap-6">
          <div className={`text-5xl font-bold ${score >= 80 ? 'text-green-400' : score >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>{score}<span className="text-xl text-gray-500">/100</span></div>
          <div className="text-gray-300">
            <div className="font-semibold">Score de robustesse</div>
            <div className="text-sm text-gray-400">{vulnCount} vulnérabilité(s) sur {findings.length} sonde(s) — OWASP LLM Top 10</div>
          </div>
        </div>
      )}

      {/* Findings */}
      <div className="space-y-3">
        {findings.map((f, i) => (
          <div key={i} className={`bg-gray-800 border rounded-xl p-4 ${sevColor(f.severity, f.vulnerable)}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-medium text-gray-100">
                {f.vulnerable ? <ShieldAlert className="w-4 h-4 text-red-500" /> : <ShieldCheck className="w-4 h-4 text-green-500" />}
                {f.name} <span className="text-xs text-gray-500">[{f.owasp_id}]</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded ${f.vulnerable ? 'bg-red-900/50 text-red-300' : 'bg-green-900/40 text-green-300'}`}>
                {f.vulnerable ? `VULNÉRABLE · ${f.severity}` : 'ROBUSTE'} · {f.confidence}%
              </span>
            </div>
            {f.explanation && <div className="mt-2 text-sm text-gray-400">{f.explanation}</div>}
            {f.target_response && <pre className="mt-2 text-xs text-gray-500 bg-gray-900/60 rounded p-2 overflow-x-auto whitespace-pre-wrap">{f.target_response}</pre>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChatbotPentest;
